import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CampusHeader from '../../../components/feature/CampusHeader';
import Footer from '../../../components/feature/Footer';
import { sendPasswordResetCode } from '../../../services/email';

const VerifyResetCodePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialEmail = useMemo(() => params.get('email') || '', [params]);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (initialEmail && !email) setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleDigitChange = (index: number, raw: string) => {
    const digits = raw.replace(/\D+/g, '');
    if (!digits) {
      const next = [...code];
      next[index] = '';
      setCode(next);
      return;
    }
    const next = [...code];
    for (let i = 0; i < digits.length && index + i < 6; i++) {
      next[index + i] = digits[i];
    }
    setCode(next);
    inputsRef.current[Math.min(index + digits.length, 5)]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) inputsRef.current[index - 1]?.focus();
    else if (e.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
    else if (e.key === 'ArrowRight' && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D+/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setCode(text.split(''));
      inputsRef.current[5]?.focus();
    }
  };

  const submit = () => {
    setError(null);
    const trimmedEmail = email.trim();
    const trimmedCode = code.join('');
    if (!trimmedEmail) { setError('Enter your email.'); return; }
    if (!/^\d{6}$/.test(trimmedCode)) { setError('Enter the 6-digit code.'); return; }
    // Don't verify on the server here — the next page sets the new password and
    // verifies the code atomically.
    navigate(`/auth/reset-password-code/new?email=${encodeURIComponent(trimmedEmail)}&code=${encodeURIComponent(trimmedCode)}`);
  };

  const resend = async () => {
    setError(null);
    setInfo(null);
    const trimmed = email.trim();
    if (!trimmed) { setError('Enter your email first.'); return; }
    if (cooldown > 0) return;
    setResending(true);
    try {
      await sendPasswordResetCode(trimmed);
      setInfo('A new reset code has been sent. Check your inbox (and spam).');
      setCooldown(60);
    } catch (e: any) {
      setError(e?.message || 'Failed to resend code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      <main className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">Enter Reset Code</h1>
          <p className="text-gray-600 mb-6 text-center">We sent a 6-digit code to your email.</p>
          <div className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />

            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputsRef.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
                />
              ))}
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {info && !error && <p className="text-sm text-green-700">{info}</p>}

            <button
              onClick={submit}
              disabled={code.join('').length !== 6}
              className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-60"
            >
              Continue
            </button>
            <button
              onClick={resend}
              disabled={resending || cooldown > 0}
              className="w-full px-4 py-3 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {resending ? 'Sending…' : cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
            <button
              onClick={() => navigate('/auth/forgot-password')}
              className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VerifyResetCodePage;
